import React, { PropsWithChildren, useRef, useState } from "react";
import { toast } from "react-toastify";
import { useGroupContext } from "../../../lib/context/Groups/GroupContext";
import { useOutsideClickDetection } from "../../../lib/hooks/useOutsideClickDetection";
import { useToggleModal } from "../../../lib/hooks/useToggleModal";
import { useRouter } from "../../../lib/hooks/useRouter";
import { useGroupsRequests } from "../../../api/grpc/permissions/useGroupsRequests";
import { useGroupFormik } from "./lib/useGroupFormik";

import { DoubleButton } from "../../shared/DoubleButton/DoubleButton";
import { DropdownList } from "../../shared/DropdownList/DropdownList";
import { Icon } from "../../shared/Icon/Icon";
import { GroupForm } from "./GroupForm";
import { PermissionGroupDetailedResponse } from "../../../api/grpc/permissions/permissions";

import { WorkplaceFields } from "../../../api/graphql/workplaces";
import { Button } from "../../shared/Button/Button";
import { USERS_GROUPS_ROOT_PATH } from "../../../lib/routes/routes";

export const editGroupButtonOptions = [
  {
    icon: "delete-default",
    label: "Delete",
    type: "delete",
  },
];

export const EditGroupForm = ({
  groupData,
  refetchGroup,
}: PropsWithChildren<{
  groupData: PermissionGroupDetailedResponse;
  refetchGroup: () => void;
}>) => {
  const ref = useRef(null);
  const { isOpen, toggleModal } = useToggleModal();
  const [loading, setLoading] = useState(false);
  const { refetchGroups } = useGroupContext();
  const { history } = useRouter();
  const { deletePermissionGroup, updatePermissionGroup } = useGroupsRequests();

  useOutsideClickDetection(
    ref,
    () => {
      if (!isOpen) {
        return;
      }
      toggleModal();
    },
    isOpen
  );

  const formik = useGroupFormik({
    initialValues: {
      name: groupData.name,
      description: groupData.description,
      accessEverything: groupData.accessEverything,
      rooms: groupData.rooms as WorkplaceFields[],
      desks: groupData.desks.map((desk) => ({
        ...desk,
        __typename: "Desk",
      })) as WorkplaceFields[],
      locations: groupData.locations.map((location) => location.id),
      deleteMembers: [],
      addMembers: [],
      deskBookingEnabled: groupData.deskBookingEnabled,
      roomBookingEnabled: groupData.roomBookingEnabled,
      id: groupData.id,
      isDefault: groupData.isDefault,
    },
    onSubmit: async (values) => {
      try {
        setLoading(true);

        await updatePermissionGroup({
          id: groupData.id,
          name: values.name,
          description: values.description,
          accessEverything: values.accessEverything,
          rooms: values.rooms.map((room) => room.id),
          desks: values.desks.map((desk) => desk.id),
          locations: values.locations || [],
          deleteMembers: values.deleteMembers || [],
          addMembers: values.addMembers || [],
          deskBookingEnabled: values.deskBookingEnabled,
          roomBookingEnabled: values.roomBookingEnabled,
        });

        refetchGroup();
        await refetchGroups();
        toast.success("Group updated successfully.");
      } catch (error: any) {
        toast.error(
          error?.message || "Failed to update group. Please try again."
        );
      } finally {
        setLoading(false);
      }
    },
  });

  const handleDeleteGroup = async () => {
    try {
      toggleModal();

      await deletePermissionGroup({ id: groupData.id });

      toast.success("Group deleted successfully.");

      await refetchGroups();
      history.push(USERS_GROUPS_ROOT_PATH);
    } catch (error: any) {
      toast.error(
        error?.message || "Failed to delete group. Please try again."
      );
    }
  };

  return (
    <>
      <div className="main-flex mb-3 TemplateManage__head">
        <div className="TemplateManage__info">
          <Icon icon="red-error-icon" />
          <span>
            Note that when a user is added to a group, they will only have
            access to items that the group has access to.
          </span>
        </div>

        <div className="flex-a-center gap-1 TemplateManage__head--buttons">
          <Button
            title="Cancel"
            color="outline-secondary"
            size="small"
            className="TemplateManage__head--cancel"
            onClick={() => history.push(USERS_GROUPS_ROOT_PATH)}
          />
          <div ref={ref} className="TemplateManage__buttons relative">
            <DoubleButton
              onClick={() => formik.handleSubmit()}
              secondButtonClick={toggleModal}
              disabled={loading}
            />

            {isOpen && (
              <DropdownList
                onClick={handleDeleteGroup}
                options={editGroupButtonOptions}
              />
            )}
          </div>
        </div>
      </div>

      <GroupForm formik={formik} />
    </>
  );
};
